import { useCallback, useSyncExternalStore } from 'react';
import {
  getDirectThreadSnapshot,
  subscribeDirectThreadMeta,
} from '@/components/Messenger/cache/directThreadCacheStore';

export type DirectThreadMetaSnapshot = {
  lastReadMessageId: string | null;
  loading: boolean;
  loadingOlder: boolean;
  loadingNewer: boolean;
  error: string | null;
};

const EMPTY: DirectThreadMetaSnapshot = Object.freeze({
  lastReadMessageId: null,
  loading: false,
  loadingOlder: false,
  loadingNewer: false,
  error: null,
});

const cacheByThread = new Map<string, DirectThreadMetaSnapshot>();

function getSnapshot(threadId: string | null): DirectThreadMetaSnapshot {
  if (!threadId) return EMPTY;
  const raw = getDirectThreadSnapshot(threadId);
  const prev = cacheByThread.get(threadId);
  if (
    prev &&
    prev.lastReadMessageId === raw.lastReadMessageId &&
    prev.loading === raw.loading &&
    prev.loadingOlder === raw.loadingOlder &&
    prev.loadingNewer === raw.loadingNewer &&
    prev.error === raw.error
  ) {
    return prev;
  }
  const next: DirectThreadMetaSnapshot = {
    lastReadMessageId: raw.lastReadMessageId,
    loading: raw.loading,
    loadingOlder: raw.loadingOlder,
    loadingNewer: raw.loadingNewer,
    error: raw.error,
  };
  cacheByThread.set(threadId, next);
  return next;
}

export function useDirectThreadMeta(threadId: string | null): DirectThreadMetaSnapshot {
  return useSyncExternalStore(
    useCallback((onChange) => subscribeDirectThreadMeta(threadId, onChange), [threadId]),
    () => getSnapshot(threadId),
    () => EMPTY
  );
}
